import { useRouter } from 'next/router';
import { useMemo, useState } from 'react';
import useLocalStorage from 'use-local-storage';
import { CompareProduct } from '../../utils/types/types';
import { fetchCompareData } from '../../api/compare';
import { useMedia } from '../../hooks/useMedia';
import constants from '../../utils/constants';

const NVAL_REGEX = constants.NVAL_REGEX;

export const LOCAL_STORAGE_KEY = 'compare:data';

const initialSelected = {
  products: [],
  category: null,
};

type Selected = {
  products: string[];
  category?: string;
};

interface Phase {
  loading?: boolean;
  error?: boolean;
  info?: boolean;
}

export interface ContextProps {
  data: Array<CompareProduct> | null;
  open: () => void;
  close: () => void;
  load: (ids?: string[]) => Promise<void> | void;
  toggle: (id: string, category: string) => Promise<void> | void;
  removeAll: () => void;
  storageKey: string;
  isOpen: boolean;
  max: number;
  isDisabled: boolean;
  products: string[];
  category: string | null;
  loading?: boolean;
  error?: boolean;
}

export const createStorageKey = (router) => {
  const { query, asPath } = router;

  const { type, 'search-term': term } = query;
  const isSearch = type === 'search';

  if (isSearch) {
    const searchTerm = term;

    if (!searchTerm) {
      return 'unknown';
    }
    return searchTerm.toLowerCase();
  }

  const [, value] = asPath.match(NVAL_REGEX) || [];

  if (!value) {
    return 'unknown';
  }

  return value;
};

export const useCompareDataInitialiser = () => {
  const router = useRouter();

  const storageKey = useMemo(() => createStorageKey(router), [router]);

  const maxCompare = useMedia(
    ['(min-width: 768px)', '(min-width: 375px)', '(min-width: 0px)'],
    [4, 3, 2],
    4
  );

  const [isOpen, setIsOpen] = useState(false);

  const [store, setStore] = useLocalStorage(
    LOCAL_STORAGE_KEY,
    {},
    { logger: process.env.NODE_ENV === 'test' ? () => {} : console.log }
  );

  const selected = store[storageKey] || initialSelected;

  const setSelected = (data: Selected) =>
    setStore({
      ...store,
      [storageKey]: data,
    });

  const [data, setData] = useState(null);

  const [phase, setPhase] = useState<Phase>({});

  const load = async (products = selected.products) => {
    let productsData = data;

    if (products.length > 1 && products.length <= 4) {
      try {
        setPhase({ loading: true, error: false });
        const response = await fetchCompareData(products.join(','));
        productsData = response.products;
        setPhase({ loading: false, error: false });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (err) {
        setPhase({ loading: false, error: true });
      }
    }

    if (!productsData) {
      return;
    }

    const filteredProducts = productsData.filter(({ productId }) =>
      products.includes(productId)
    );

    setData(filteredProducts);
  };

  const toggle = (id, category) => {
    const { category: currentCategory, products } = selected;

    let updatedProducts = products;

    if (!products.includes(id)) {
      updatedProducts = products.concat([id]);
    } else {
      updatedProducts = products.filter((val) => val !== id);
    }

    if (updatedProducts.length === 0 && isOpen) {
      setIsOpen(false);
    }

    setSelected({
      products: updatedProducts,
      category: category || currentCategory,
    });

    if (isOpen) {
      return load(updatedProducts);
    }

    return Promise.resolve();
  };

  const removeAll = () => {
    setSelected({ products: [] });
    setData(null);
    setIsOpen(false);
  };

  const open = () => setIsOpen(true);

  const close = () => setIsOpen(false);

  const context = useMemo(
    () => ({
      data,
      open,
      close,
      load,
      toggle,
      removeAll,
      storageKey,
      isOpen,
      max: maxCompare,
      isDisabled: selected.products.length >= maxCompare,
      products: selected.products,
      category: selected.category,
      loading: phase.loading,
      error: phase.error,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phase, selected, data, isOpen]
  );

  return context;
};
